import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, MoreHorizontalIcon, SettingsIcon, XIcon, PenIcon, PlusIcon, TrashIcon, ChevronDownIcon, CheckIcon, AlertTriangleIcon, ArchiveIcon, RefreshCcwIcon, CheckCircleIcon, XCircleIcon, UserPlusIcon, UploadIcon } from 'lucide-react';
import { mockBills, approveBill, rejectBill, updateBill } from '../utils/mockBills';
type TabType = 'due' | 'pending' | 'scheduled' | 'paid' | 'assigned';
interface Threshold {
  id: string;
  approver: string;
  amount: number;
  currency: string;
}
// Cycle day options
const CYCLE_DAY_OPTIONS = [{
  value: 1,
  label: '1st of the month'
}, {
  value: 5,
  label: '5th of the month'
}, {
  value: 10,
  label: '10th of the month'
}, {
  value: 15,
  label: '15th of the month'
}, {
  value: 20,
  label: '20th of the month'
}, {
  value: 25,
  label: '25th of the month'
}, {
  value: -1,
  label: 'Last day of the month'
}];
export function Bills() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('due');
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selectedBillIds, setSelectedBillIds] = useState<Set<string>>(new Set());
  // Force re-read of mutable mockBills on every render
  const [refreshKey, setRefreshKey] = useState(0);
  // Settings panel state
  const [showSettings, setShowSettings] = useState(false);
  const [paymentCycles, setPaymentCycles] = useState<number[]>(() => {
    const stored = localStorage.getItem('billPaymentCycles');
    return stored ? JSON.parse(stored) : [];
  });
  const [numberOfApprovals, setNumberOfApprovals] = useState(0);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  // Modal states
  const [showApprovalsModal, setShowApprovalsModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showCyclesModal, setShowCyclesModal] = useState(false);
  const [tempApprovals, setTempApprovals] = useState(0);
  const [tempThreshold, setTempThreshold] = useState({
    approver: 'Simon Verraest',
    amount: 0,
    currency: ''
  });
  const [tempCycles, setTempCycles] = useState<number[]>([]);
  // Action Modals State
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectAction, setRejectAction] = useState<'archive' | 'due'>('archive');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelAction, setCancelAction] = useState<'archive' | 'due'>('archive');
  const [contextMenuOpenId, setContextMenuOpenId] = useState<string | null>(null);
  const [actionTargetId, setActionTargetId] = useState<string | null>(null);
  // Current user for approval logic
  const currentUser = 'Simon Verraest';
  // Whether the current tab supports multi-select
  const showCheckboxes = activeTab !== 'paid';
  // Check if a bill is selectable (for pending tab, only bills the user can approve)
  const isBillSelectable = (bill: (typeof mockBills)[0]) => {
    if (activeTab === 'paid') return false;
    if (activeTab === 'pending') {
      // Only bills assigned to current user or unassigned (open-ended by rules)
      return !bill.approver || bill.approver === currentUser;
    }
    return true;
  };
  // Filter bills based on active tab and search
  const filteredBills = useMemo(() => {
    return mockBills.filter(bill => {
      const matchesSearch = searchQuery === '' || bill.billNumber.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierName.toLowerCase().includes(searchQuery.toLowerCase()) || bill.supplierEmail.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;
      switch (activeTab) {
        case 'due':
          return bill.status === 'approved' || bill.status === 'overdue';
        case 'pending':
          return bill.status === 'pending_approval' && (!bill.approver || bill.approver === currentUser);
        case 'scheduled':
          return bill.status === 'approved' && bill.approvalStatus === 'approved';
        case 'paid':
          return bill.status === 'paid';
        case 'assigned':
          return bill.approver === 'Simon Verraest' && bill.status === 'pending_approval';
        default:
          return true;
      }
    });
  }, [activeTab, searchQuery, refreshKey]);
  // Refresh data when component mounts (e.g. after navigating back from approval wizard)
  useEffect(() => {
    setRefreshKey(k => k + 1);
  }, []);
  const groupedBills = useMemo(() => {
    const groups: Record<string, typeof filteredBills> = {
      'Due next month': [],
      'Due this month': [],
      Older: []
    };
    filteredBills.forEach(bill => {
      if (bill.dueDate.includes('2026-04')) {
        groups['Due next month'].push(bill);
      } else if (bill.dueDate.includes('2026-03')) {
        groups['Due this month'].push(bill);
      } else {
        groups['Older'].push(bill);
      }
    });
    return Object.entries(groups).filter(([_, bills]) => bills.length > 0);
  }, [filteredBills]);
  const handleSelectAllInGroup = (groupBills: typeof filteredBills) => {
    const selectableBills = groupBills.filter(isBillSelectable);
    const allSelected = selectableBills.every(b => selectedBillIds.has(b.id));
    const newSelected = new Set(selectedBillIds);
    if (allSelected) {
      selectableBills.forEach(b => newSelected.delete(b.id));
    } else {
      selectableBills.forEach(b => newSelected.add(b.id));
    }
    setSelectedBillIds(newSelected);
  };
  const toggleBillSelection = (id: string) => {
    const bill = mockBills.find(b => b.id === id);
    if (bill && !isBillSelectable(bill)) return;
    const newSelected = new Set(selectedBillIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedBillIds(newSelected);
  };
  const selectedBills = mockBills.filter(b => selectedBillIds.has(b.id));
  const totalSelectedAmount = selectedBills.reduce((sum, b) => sum + b.amount, 0);
  const handleSchedulePayments = () => {
    if (selectedBillIds.size === 0) return;
    const idsParam = Array.from(selectedBillIds).join(',');
    navigate(`/bill-approval/schedule?ids=${idsParam}`);
  };
  const handleApproveBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowApproveModal(true);
    setContextMenuOpenId(null);
  };
  const confirmApproveBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => approveBill(id, currentUser));
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowApproveModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleRejectBills = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowRejectModal(true);
    setContextMenuOpenId(null);
  };
  const confirmRejectBills = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (rejectAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due (approved status without schedule)
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowRejectModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleCancelScheduled = (id?: string) => {
    if (id) setActionTargetId(id);else if (selectedBillIds.size === 0) return;
    setShowCancelModal(true);
    setContextMenuOpenId(null);
  };
  const confirmCancelScheduled = () => {
    const idsToProcess = actionTargetId ? [actionTargetId] : Array.from(selectedBillIds);
    idsToProcess.forEach(id => {
      if (cancelAction === 'archive') {
        rejectBill(id, currentUser, 'Archived');
      } else {
        // Move back to due
        updateBill(id, {
          status: 'approved',
          approvalStatus: 'approved',
          approver: undefined
        });
      }
    });
    if (!actionTargetId) setSelectedBillIds(new Set());
    setActionTargetId(null);
    setShowCancelModal(false);
    setRefreshKey(k => k + 1);
  };
  const handleArchiveBill = (id: string) => {
    rejectBill(id, currentUser, 'Archived');
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  const handleReassignBill = (id: string) => {
    // Mock reassignment by clearing the approver so it goes back to general pending pool
    updateBill(id, {
      approver: undefined
    });
    setContextMenuOpenId(null);
    setRefreshKey(k => k + 1);
  };
  // Whether the current tab shows approve/reject actions
  const isApprovalTab = activeTab === 'assigned' || activeTab === 'pending';
  // Get summary label based on active tab
  const getSummaryLabel = () => {
    switch (activeTab) {
      case 'assigned':
      case 'pending':
        return 'Bills to review';
      case 'scheduled':
        return 'Scheduled payments';
      default:
        return 'Payments to schedule';
    }
  };
  const summaryLabel = getSummaryLabel();
  const handleSaveApprovals = () => {
    setNumberOfApprovals(tempApprovals);
    setShowApprovalsModal(false);
  };
  const handleSaveThreshold = () => {
    if (!tempThreshold.currency) return;
    setThresholds(prev => [...prev, {
      id: Date.now().toString(),
      approver: tempThreshold.approver,
      amount: tempThreshold.amount,
      currency: tempThreshold.currency
    }]);
    setTempThreshold({
      approver: 'Simon Verraest',
      amount: 0,
      currency: ''
    });
    setShowThresholdModal(false);
  };
  const removeThreshold = (id: string) => {
    setThresholds(prev => prev.filter(t => t.id !== id));
  };
  // Persist cycles to localStorage
  const handleSaveCycles = () => {
    localStorage.setItem('billPaymentCycles', JSON.stringify(tempCycles));
    setPaymentCycles(tempCycles);
    setShowCyclesModal(false);
  };
  const toggleCycleDay = (day: number) => {
    setTempCycles(prev => {
      if (prev.includes(day)) {
        return prev.filter(d => d !== day);
      }
      if (prev.length >= 2) return prev; // max 2
      return [...prev, day].sort((a, b) => a - b);
    });
  };
  const formatCycleLabel = (cycles: number[]) => {
    if (cycles.length === 0) return 'No payment cycles';
    return cycles.map(d => {
      const opt = CYCLE_DAY_OPTIONS.find(o => o.value === d);
      return opt ? opt.label : '';
    }).join(' & ');
  };
  return <div className="max-w-[1400px] mx-auto" data-id="element-5575">
      {/* Header */}
      <div className="flex justify-between items-center mb-8" data-id="element-5576">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5577">Bills</h1>
        <div className="flex items-center gap-3" data-id="element-5578">
          <button onClick={() => setShowSettings(true)} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-5579">
            <SettingsIcon size={18} data-id="element-5580" />
          </button>
          <button onClick={() => navigate('/bill-bulk-upload')} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-5581">
            <UploadIcon size={16} data-id="element-5582" />
            Upload
          </button>
          <button onClick={() => navigate('/bill-form')} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-5583">
            Add bill
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-8 items-start" data-id="element-5584">
        {/* Main Content */}
        <div className="xl:col-span-3 space-y-6" data-id="element-5585">
          {/* Tabs + Controls */}
          <div className="flex items-end justify-between border-b border-gray-200" data-id="element-5586">
            <div className="flex space-x-6" data-id="element-5587">
              {[{
              id: 'due',
              label: 'Due'
            }, {
              id: 'pending',
              label: 'Pending approval'
            }, {
              id: 'scheduled',
              label: 'Scheduled'
            }, {
              id: 'paid',
              label: 'Paid'
            }, {
              id: 'assigned',
              label: 'Assigned to me'
            }].map(tab => <button key={tab.id} onClick={() => {
              setActiveTab(tab.id as TabType);
              setSelectedBillIds(new Set());
            }} className={`pb-4 -mb-px text-sm font-medium transition-colors border-b-2 ${activeTab === tab.id ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-5588">
                  {tab.label}
                </button>)}
            </div>
            <div className="flex items-center gap-4 pb-3" data-id="element-5589">
              <div className="relative" data-id="element-5590">
                <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-5591" />
                <input type="text" placeholder="Search bills..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-56 pl-9 pr-4 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-5592" />
              </div>
              <label className="flex items-center cursor-pointer" data-id="element-5593">
                <div className="relative" data-id="element-5594">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-5595" />
                  <div className={`block w-9 h-5 rounded-full transition-colors ${showArchived ? 'bg-[#F7941D]' : 'bg-gray-200'}`} data-id="element-5596" />
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-5597" />
                </div>
                <span className="text-xs text-gray-600 ml-2 font-medium" data-id="element-5598">
                  Show archived
                </span>
              </label>
            </div>
          </div>

          {/* Bills List */}
          <div className="space-y-8" data-id="element-5599">
            {groupedBills.length === 0 ? <div className="text-center py-12 bg-white rounded-xl border border-gray-200" data-id="element-5600">
                <p className="text-gray-500 text-sm" data-id="element-5601">
                  No bills found matching your criteria.
                </p>
              </div> : groupedBills.map(([groupName, groupBills]) => <div key={groupName} className="space-y-3" data-id="element-5602">
                  <div className="flex items-center gap-3" data-id="element-5603">
                    <h3 className="text-sm font-semibold text-gray-900" data-id="element-5604">
                      {groupName}
                    </h3>
                    {showCheckboxes && <button onClick={() => handleSelectAllInGroup(groupBills)} className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06]" data-id="element-5605">
                        Select
                      </button>}
                  </div>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-5606">
                    <table className="w-full text-left border-collapse" data-id="element-5607">
                      <tbody className="divide-y divide-gray-100" data-id="element-5608">
                        {groupBills.map(bill => {
                    const selectable = isBillSelectable(bill);
                    return <tr key={bill.id} className={`hover:bg-gray-50 transition-colors ${selectedBillIds.has(bill.id) ? 'bg-orange-50/50' : ''}`} data-id="element-5609">
                              {showCheckboxes && <td className="px-4 py-4 w-12" data-id="element-5610">
                                  {selectable ? <input type="checkbox" checked={selectedBillIds.has(bill.id)} onChange={() => toggleBillSelection(bill.id)} className="w-4 h-4 text-[#F7941D] border-gray-300 rounded focus:ring-[#F7941D] cursor-pointer" data-id="element-5611" /> : <input type="checkbox" disabled className="w-4 h-4 border-gray-200 rounded opacity-30 cursor-not-allowed" data-id="element-5612" />}
                                </td>}
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5613">
                                <div className="text-sm font-semibold text-gray-900" data-id="element-5614">
                                  {bill.supplierName}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5" data-id="element-5615">
                                  {bill.billNumber}
                                </div>
                              </td>
                              <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5616">
                                <div className="text-sm font-medium text-gray-900" data-id="element-5617">
                                  THB{' '}
                                  {bill.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2
                          })}
                                </div>
                              </td>
                              {activeTab === 'paid' ? <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5618">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-5619">
                                      Source
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-5620">
                                      {bill.paymentSource === 'balance' ? 'Balance' : bill.paymentSource === 'card' ? 'Card' : bill.paymentSource === 'bank_transfer' ? 'Bank Transfer' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5621">
                                    <div className="text-xs text-gray-500 uppercase tracking-wide font-medium" data-id="element-5622">
                                      Payout
                                    </div>
                                    <div className="text-sm text-gray-900 mt-0.5" data-id="element-5623">
                                      {bill.payoutType === 'bank_account' ? 'Bank Account' : bill.payoutType === 'promptpay' ? 'PromptPay' : '—'}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5624">
                                    <div className="text-sm text-gray-500" data-id="element-5625">
                                      Paid on{' '}
                                      {bill.paidDate ? new Date(bill.paidDate).toLocaleDateString('en-GB') : '—'}
                                    </div>
                                  </td>
                                </> : <>
                                  <td className="px-4 py-4 cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5626">
                                    <div className="flex flex-col items-start gap-1" data-id="element-5627">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5628">
                                        {bill.status === 'pending_approval' ? 'Pending' : bill.status === 'approved' ? 'Due' : bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                                      </span>
                                      {bill.status === 'pending_approval' && numberOfApprovals > 1 && <span className="text-[10px] text-gray-500 font-medium bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100" data-id="element-5629">
                                            Requires {numberOfApprovals}{' '}
                                            approvals
                                          </span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right cursor-pointer" onClick={() => navigate(`/bill-detail/${bill.id}`)} data-id="element-5630">
                                    <div className="text-sm text-gray-500" data-id="element-5631">
                                      Due on{' '}
                                      {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                                    </div>
                                  </td>
                                </>}
                              <td className="px-4 py-4 text-right w-12 relative" data-id="element-5632">
                                {activeTab !== 'paid' && <>
                                    <button onClick={e => {
                            e.stopPropagation();
                            setContextMenuOpenId(contextMenuOpenId === bill.id ? null : bill.id);
                          }} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors" data-id="element-5633">
                                      <MoreHorizontalIcon size={18} data-id="element-5634" />
                                    </button>

                                    {contextMenuOpenId === bill.id && <>
                                        <div className="fixed inset-0 z-10" onClick={e => {
                              e.stopPropagation();
                              setContextMenuOpenId(null);
                            }} data-id="element-5635" />
                                        <div className="absolute right-8 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20 animate-in fade-in zoom-in-95 duration-100" data-id="element-5636">
                                          {activeTab === 'due' && <button onClick={e => {
                                e.stopPropagation();
                                handleArchiveBill(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-5637">
                                              <ArchiveIcon size={14} className="mr-2" data-id="element-5638" />
                                              Archive bill
                                            </button>}

                                          {activeTab === 'pending' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-5639">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-5640" />
                                                Approve
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-5641">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-5642" />
                                                Cancel
                                              </button>
                                            </>}

                                          {activeTab === 'scheduled' && <button onClick={e => {
                                e.stopPropagation();
                                handleCancelScheduled(bill.id);
                              }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-5643">
                                              <XCircleIcon size={14} className="mr-2" data-id="element-5644" />
                                              Cancel payment
                                            </button>}

                                          {activeTab === 'assigned' && <>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleApproveBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-5645">
                                                <CheckCircleIcon size={14} className="mr-2 text-green-600" data-id="element-5646" />
                                                Approve
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleReassignBill(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center" data-id="element-5647">
                                                <UserPlusIcon size={14} className="mr-2 text-blue-600" data-id="element-5648" />
                                                Re-assign
                                              </button>
                                              <button onClick={e => {
                                  e.stopPropagation();
                                  handleRejectBills(bill.id);
                                }} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center" data-id="element-5649">
                                                <XCircleIcon size={14} className="mr-2" data-id="element-5650" />
                                                Cancel
                                              </button>
                                            </>}
                                        </div>
                                      </>}
                                  </>}
                              </td>
                            </tr>;
                  })}
                      </tbody>
                    </table>
                  </div>
                </div>)}
          </div>
        </div>

        {/* Right Sidebar (Summary) */}
        <div className="xl:col-span-1" data-id="element-5651">
          <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 sticky top-6" data-id="element-5652">
            <div className="flex justify-between items-start mb-6" data-id="element-5653">
              <div data-id="element-5654">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-5655">Summary</h2>
                <p className="text-xs text-gray-500 mt-1" data-id="element-5656">
                  Based on your selected bills
                </p>
              </div>
              {selectedBillIds.size > 0 && <button onClick={() => setSelectedBillIds(new Set())} className="text-xs font-medium text-gray-400 hover:text-gray-600 transition-colors" data-id="element-5657">
                  Clear
                </button>}
            </div>
            <div className="space-y-4 mb-8" data-id="element-5658">
              <div className="flex justify-between items-center text-sm" data-id="element-5659">
                <span className="text-gray-600" data-id="element-5660">
                  {summaryLabel} - {selectedBillIds.size}
                </span>
                {selectedBillIds.size === 0 && <span className="text-gray-400 text-xs" data-id="element-5661">
                    No selected bills
                  </span>}
              </div>
              {selectedBillIds.size > 0 && <div className="flex justify-between items-center pt-4 border-t border-gray-200" data-id="element-5662">
                  <span className="text-sm font-semibold text-gray-900" data-id="element-5663">
                    Total
                  </span>
                  <span className="text-sm font-bold text-gray-900" data-id="element-5664">
                    THB{' '}
                    {totalSelectedAmount.toLocaleString('en-US', {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>}
            </div>

            {isApprovalTab ? <div className="flex gap-2" data-id="element-5665">
                <button onClick={() => handleRejectBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-white border border-red-300 text-red-600 hover:bg-red-50 shadow-sm' : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed'}`} data-id="element-5666">
                  Reject
                </button>
                <button onClick={() => handleApproveBills()} disabled={selectedBillIds.size === 0} className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-green-600 hover:bg-green-700 text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-5667">
                  Approve
                </button>
              </div> : activeTab === 'scheduled' ? <button onClick={() => handleCancelScheduled()} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-red-600 hover:bg-red-700 text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-5668">
                Cancel scheduled payments
              </button> : <button onClick={handleSchedulePayments} disabled={selectedBillIds.size === 0} className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${selectedBillIds.size > 0 ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white shadow-sm' : 'bg-gray-300 text-white cursor-not-allowed'}`} data-id="element-5669">
                Schedule payments
              </button>}
          </div>
        </div>
      </div>

      {/* ========== ACTION MODALS ========== */}

      {/* Reject Modal */}
      {showRejectModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowRejectModal(false)} data-id="element-5670" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5671">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-5672">
              <div className="p-6" data-id="element-5673">
                <div className="flex items-center gap-3 mb-4" data-id="element-5674">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-5675">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-5676" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-5677">
                    Reject{' '}
                    {actionTargetId ? 'bill' : `${selectedBillIds.size} bills`}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-5678">
                  What would you like to do with the rejected{' '}
                  {actionTargetId ? 'bill' : 'bills'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-5679">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-5680">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'archive'} onChange={() => setRejectAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-5681" />
                    <div className="ml-3" data-id="element-5682">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-5683">
                        Archive bills
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-5684">
                        Remove them from the active list permanently.
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${rejectAction === 'due' ? 'border-[#F7941D] bg-orange-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-5685">
                    <input type="radio" name="rejectAction" checked={rejectAction === 'due'} onChange={() => setRejectAction('due')} className="mt-0.5 text-[#F7941D] focus:ring-[#F7941D]" data-id="element-5686" />
                    <div className="ml-3" data-id="element-5687">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-5688">
                        Move back to Due
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-5689">
                        Return them to the due list for reassessment.
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-5690">
                  <button onClick={() => setShowRejectModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5691">
                    Cancel
                  </button>
                  <button onClick={confirmRejectBills} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-5692">
                    Confirm rejection
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Approve Modal */}
      {showApproveModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApproveModal(false)} data-id="element-5693" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5694">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-5695">
              <div className="p-6 text-center" data-id="element-5696">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4" data-id="element-5697">
                  <CheckCircleIcon size={24} className="text-green-600" data-id="element-5698" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-5699">
                  Confirm Approval
                </h2>
                <p className="text-sm text-gray-600 mb-8" data-id="element-5700">
                  Are you sure you want to approve{' '}
                  {actionTargetId ? 'this bill' : `these ${selectedBillIds.size} bills`}
                  ?
                  {numberOfApprovals > 1 && ' This will count as 1 of the required approvals.'}
                </p>
                <div className="flex justify-center gap-3" data-id="element-5701">
                  <button onClick={() => setShowApproveModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5702">
                    Cancel
                  </button>
                  <button onClick={confirmApproveBills} className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors" data-id="element-5703">
                    Yes, approve
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Cancel Scheduled Modal */}
      {showCancelModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCancelModal(false)} data-id="element-5704" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5705">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-5706">
              <div className="p-6" data-id="element-5707">
                <div className="flex items-center gap-3 mb-4" data-id="element-5708">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0" data-id="element-5709">
                    <AlertTriangleIcon size={20} className="text-red-600" data-id="element-5710" />
                  </div>
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-5711">
                    Cancel Scheduled {actionTargetId ? 'Payment' : 'Payments'}
                  </h2>
                </div>

                <p className="text-sm text-gray-600 mb-6" data-id="element-5712">
                  What would you like to do with the cancelled{' '}
                  {actionTargetId ? 'payment' : 'payments'}?
                </p>

                <div className="space-y-3 mb-8" data-id="element-5713">
                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'due' ? 'border-[#F7941D] bg-orange-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-5714">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'due'} onChange={() => setCancelAction('due')} className="mt-0.5 text-[#F7941D] focus:ring-[#F7941D]" data-id="element-5715" />
                    <div className="ml-3" data-id="element-5716">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-5717">
                        Move back to Due
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-5718">
                        Return them to the due list to be scheduled later.
                      </span>
                    </div>
                  </label>

                  <label className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${cancelAction === 'archive' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-5719">
                    <input type="radio" name="cancelAction" checked={cancelAction === 'archive'} onChange={() => setCancelAction('archive')} className="mt-0.5 text-red-600 focus:ring-red-500" data-id="element-5720" />
                    <div className="ml-3" data-id="element-5721">
                      <span className="block text-sm font-medium text-gray-900" data-id="element-5722">
                        Archive bills
                      </span>
                      <span className="block text-xs text-gray-500 mt-1" data-id="element-5723">
                        Remove them from the active list permanently.
                      </span>
                    </div>
                  </label>
                </div>

                <div className="flex justify-end gap-3" data-id="element-5724">
                  <button onClick={() => setShowCancelModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5725">
                    Keep scheduled
                  </button>
                  <button onClick={confirmCancelScheduled} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors" data-id="element-5726">
                    Confirm cancellation
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== BILLS SETTINGS SLIDE-OUT PANEL ========== */}
      {showSettings && <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowSettings(false)} data-id="element-5727" />

          {/* Panel */}
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-50 shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200" data-id="element-5728">
            <div className="p-8" data-id="element-5729">
              {/* Close button */}
              <div className="flex justify-end mb-6" data-id="element-5730">
                <button onClick={() => setShowSettings(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-5731">
                  <XIcon size={18} data-id="element-5732" />
                </button>
              </div>

              <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-5733">
                Bills settings
              </h2>

              {/* Bill payments can be approved by */}
              <div className="mb-8" data-id="element-5734">
                <h3 className="text-sm font-semibold text-gray-900 mb-2" data-id="element-5735">
                  Bill payments can be approved by
                </h3>
                <p className="text-xs text-gray-500 leading-relaxed" data-id="element-5736">
                  Users in these user groups can approve bills for payment
                  scheduling. You can also assign specific users from your
                  company to approve bill payments when they exceed certain
                  thresholds.
                </p>
              </div>

              {/* Billing Cycles */}
              <div className="mb-8" data-id="element-5737">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-5738">
                  Billing cycles
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-5739">
                  <span className="text-sm font-medium text-gray-900" data-id="element-5740">
                    {formatCycleLabel(paymentCycles)}
                  </span>
                  <button onClick={() => {
                setTempCycles([...paymentCycles]);
                setShowCyclesModal(true);
              }} className="text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-5741">
                    <PenIcon size={16} data-id="element-5742" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-5743">
                  Automate your payment runs by setting your payment cycles to
                  schedule approved payments on these dates. You can select up
                  to 2 payment run days per month.
                </p>
              </div>

              {/* Number of Approvals */}
              <div className="mb-8" data-id="element-5744">
                <h3 className="text-xs font-medium text-gray-500 mb-2" data-id="element-5745">
                  Number of approvals
                </h3>
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-5746">
                  <span className="text-sm font-medium text-gray-900" data-id="element-5747">
                    {numberOfApprovals}
                  </span>
                  <button onClick={() => {
                setTempApprovals(numberOfApprovals);
                setShowApprovalsModal(true);
              }} className="text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-5748">
                    <PenIcon size={16} data-id="element-5749" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 leading-relaxed" data-id="element-5750">
                  Approving any payment for bills will require{' '}
                  {numberOfApprovals} user(s) to approve except when threshold
                  rules apply or a specific user assigned to a bill as
                  designated approver.
                </p>
              </div>

              {/* Limits and Thresholds */}
              <div data-id="element-5751">
                <div className="flex items-center justify-between mb-2" data-id="element-5752">
                  <h3 className="text-xs font-medium text-gray-500" data-id="element-5753">
                    Limits and thresholds
                  </h3>
                  <button onClick={() => {
                setTempThreshold({
                  approver: 'Simon Verraest',
                  amount: 0,
                  currency: ''
                });
                setShowThresholdModal(true);
              }} className="text-xs font-medium text-[#F7941D] hover:text-[#D97B06] transition-colors" data-id="element-5754">
                    Add threshold
                  </button>
                </div>

                {thresholds.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-5755">
                    <span className="text-sm text-gray-500" data-id="element-5756">
                      No specific threshold approvers configured yet
                    </span>
                  </div> : <div className="space-y-2" data-id="element-5757">
                    {thresholds.map(t => <div key={t.id} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3" data-id="element-5758">
                        <div data-id="element-5759">
                          <div className="text-sm font-medium text-gray-900" data-id="element-5760">
                            {t.approver}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5" data-id="element-5761">
                            Bills above {t.currency}{' '}
                            {t.amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2
                    })}
                          </div>
                        </div>
                        <button onClick={() => removeThreshold(t.id)} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-5762">
                          <TrashIcon size={14} data-id="element-5763" />
                        </button>
                      </div>)}
                  </div>}

                <p className="text-xs text-gray-500 mt-3 leading-relaxed" data-id="element-5764">
                  When threshold rules are configured, payments for bills
                  exceeding these thresholds can only be approved by the
                  assigned user instead of any number of approvals from the
                  approver group.
                </p>
              </div>
            </div>
          </div>
        </>}

      {/* ========== NUMBER OF APPROVALS MODAL ========== */}
      {showApprovalsModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowApprovalsModal(false)} data-id="element-5765" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5766">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-5767">
              <div className="p-8" data-id="element-5768">
                <div className="flex justify-end mb-4" data-id="element-5769">
                  <button onClick={() => setShowApprovalsModal(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-5770">
                    <XIcon size={18} data-id="element-5771" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-5772">
                  Number of approvals
                </h2>
                <p className="text-sm text-gray-500 mb-6 leading-relaxed" data-id="element-5773">
                  The default number of staff members that need to approve each
                  bill. You can also setup additional specific limits and
                  thresholds for senior team members.
                </p>

                <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5774">
                  Number of approvals
                </label>
                <input type="number" min="0" value={tempApprovals} onChange={e => setTempApprovals(parseInt(e.target.value) || 0)} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-5775" />

                <div className="flex justify-end gap-3 mt-8" data-id="element-5776">
                  <button onClick={() => setShowApprovalsModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5777">
                    Cancel
                  </button>
                  <button onClick={handleSaveApprovals} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors" data-id="element-5778">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD THRESHOLD MODAL ========== */}
      {showThresholdModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowThresholdModal(false)} data-id="element-5779" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5780">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-5781">
              <div className="p-8" data-id="element-5782">
                <div className="flex justify-end mb-4" data-id="element-5783">
                  <button onClick={() => setShowThresholdModal(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-5784">
                    <XIcon size={18} data-id="element-5785" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-5786">
                  Add threshold
                </h2>

                <div className="space-y-5" data-id="element-5787">
                  {/* Approver */}
                  <div data-id="element-5788">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5789">
                      Approver
                    </label>
                    <div className="relative" data-id="element-5790">
                      <select value={tempThreshold.approver} onChange={e => setTempThreshold(prev => ({
                    ...prev,
                    approver: e.target.value
                  }))} className="w-full appearance-none px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white pr-10" data-id="element-5791">
                        <option value="Simon Verraest" data-id="element-5792">Simon Verraest</option>
                        <option value="John Smith" data-id="element-5793">John Smith</option>
                        <option value="Sarah Johnson" data-id="element-5794">Sarah Johnson</option>
                      </select>
                      <ChevronDownIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-5795" />
                    </div>
                  </div>

                  {/* Amount */}
                  <div data-id="element-5796">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5797">
                      This user will need to approve bills above this amount
                    </label>
                    <input type="number" min="0" value={tempThreshold.amount} onChange={e => setTempThreshold(prev => ({
                  ...prev,
                  amount: parseFloat(e.target.value) || 0
                }))} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-5798" />
                  </div>

                  {/* Currency */}
                  <div data-id="element-5799">
                    <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-5800">
                      Select a currency for this threshold
                    </label>
                    <div className="relative" data-id="element-5801">
                      <select value={tempThreshold.currency} onChange={e => setTempThreshold(prev => ({
                    ...prev,
                    currency: e.target.value
                  }))} className="w-full appearance-none px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors bg-white pr-10" data-id="element-5802">
                        <option value="" data-id="element-5803">Select a currency</option>
                        <option value="THB" data-id="element-5804">THB</option>
                        <option value="USD" data-id="element-5805">USD</option>
                        <option value="EUR" data-id="element-5806">EUR</option>
                        <option value="GBP" data-id="element-5807">GBP</option>
                        <option value="SGD" data-id="element-5808">SGD</option>
                        <option value="MYR" data-id="element-5809">MYR</option>
                      </select>
                      <ChevronDownIcon size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-5810" />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8" data-id="element-5811">
                  <button onClick={() => setShowThresholdModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5812">
                    Cancel
                  </button>
                  <button onClick={handleSaveThreshold} disabled={!tempThreshold.currency} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-5813">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* ========== ADD CYCLES MODAL ========== */}
      {showCyclesModal && <>
          <div className="fixed inset-0 bg-black/50 z-[60]" onClick={() => setShowCyclesModal(false)} data-id="element-5814" />
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-5815">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-id="element-5816">
              <div className="p-8" data-id="element-5817">
                <div className="flex justify-end mb-4" data-id="element-5818">
                  <button onClick={() => setShowCyclesModal(false)} className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors" data-id="element-5819">
                    <XIcon size={18} data-id="element-5820" />
                  </button>
                </div>

                <h2 className="text-lg font-bold text-gray-900 mb-6" data-id="element-5821">
                  Payment cycles
                </h2>

                <div className="space-y-4" data-id="element-5822">
                  {CYCLE_DAY_OPTIONS.map(opt => <label key={opt.value} className="flex items-center cursor-pointer" data-id="element-5823">
                      <input type="checkbox" className="w-4 h-4 text-[#F7941D] border-gray-300 rounded focus:ring-[#F7941D] cursor-pointer" checked={tempCycles.includes(opt.value)} onChange={() => toggleCycleDay(opt.value)} data-id="element-5824" />
                      <span className="ml-3 text-sm text-gray-700" data-id="element-5825">
                        {opt.label}
                      </span>
                    </label>)}
                </div>

                <div className="flex justify-end gap-3 mt-8" data-id="element-5826">
                  <button onClick={() => setShowCyclesModal(false)} className="px-6 py-2.5 text-sm font-medium text-[#F7941D] bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-5827">
                    Cancel
                  </button>
                  <button onClick={handleSaveCycles} className="px-6 py-2.5 text-sm font-medium text-white bg-gray-400 rounded-lg hover:bg-gray-500 transition-colors" data-id="element-5828">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}